import { Link, usePage } from '@inertiajs/react'
import { BookOpen, ExternalLink } from 'lucide-react'
import type { ReactNode } from 'react'
import { Tooltip } from '@/components/ui/Tooltip'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import { cn } from '@/lib/utils'

interface HelpCenterHeaderProps {
  actions?: ReactNode
}

const TABS = [
  { label: 'Content', href: (params: Record<string, string>) => routes.helpArticles.index.path(params) },
  { label: 'Banners', href: (params: Record<string, string>) => routes.helpBanners.index.path(params) },
]

export function HelpCenterHeader({ actions }: HelpCenterHeaderProps) {
  const { withAccount } = useRouteParams()
  const accountSlug = withAccount().account_id
  const { url } = usePage()
  // Prefer the live custom domain (set by the controller); fall back to the
  // hosted /help/:slug URL when no custom domain is live.
  const { public_help_url: publicHelpUrl } = usePage<{ public_help_url?: string | null }>().props
  const helpCenterHref = publicHelpUrl ?? `/help/${accountSlug}`

  return (
    <div className="mb-6 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <BookOpen className="size-6 text-muted-foreground" />
        <h2 className="text-2xl font-semibold">Help center</h2>

        <nav className="ml-3 flex items-center gap-1">
          {TABS.map(tab => {
            const href = tab.href(withAccount() as Record<string, string>)
            return (
              <Link
                key={tab.label}
                href={href}
                className={cn(
                  'cursor-pointer rounded-md px-3 py-1.5 text-sm transition-colors',
                  url.split('?')[0] === href
                    ? 'bg-muted font-medium text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {tab.label}
              </Link>
            )
          })}
        </nav>
      </div>

      <div className="flex items-center gap-2">
        {accountSlug && (
          <Tooltip content="Open your public help center in a new tab">
            <a
              href={helpCenterHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex cursor-pointer items-center gap-1.5 px-3 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <ExternalLink className="size-4" /> View help center
            </a>
          </Tooltip>
        )}
        {actions}
      </div>
    </div>
  )
}
